import moment from 'moment';
import { BookingReasonDeclinedLabel } from '../constants/securspace-constants';
import type { AssistantAudience } from './assistantCapabilities';
import { invoicePageLink } from './invoiceStatus';
import type { KnowledgeAnswer, KnowledgeBreakdownRow, KnowledgeContext, KnowledgeLink, KnowledgeQuery } from './knowledgeRegistry';
import { breakdownRows, displayDate, displayDateRange, enumLabel, money } from './lookupFormat';
import { claimsLookup, extractLabelledBookingNumber } from './lookupRouting';
import { searchBookingByNumber, type BookingLookupRow } from './requests/booking-lookup-requests';

/**
 * Looks up one booking by its number: "status of booking 1021445", "who booked
 * 1021445", "when does booking 1021445 end", "why was booking 1021445 declined".
 *
 * The bookings pages answer this, but only once the user has picked the right
 * tab — a declined booking is not on the Active tab a page opens to — and staff
 * have no way to open a booking by number at all. The lookup reads the one row,
 * says in a sentence what state the booking is in and what that means for the
 * person asking, and links to the page with the booking already filtered and on
 * the right tab.
 *
 * Questions about a booking's money ("what has booking 1021445 been charged")
 * and its failed payments go to their own lookups; see {@link ./lookupRouting}.
 */

const BARE_NUMBER = /\b(\d{5,})\b/;
const SLASH_DAY = 'MM/DD/YYYY';

const bookingNumberIn = (message: string): string | null =>
  extractLabelledBookingNumber(message) ?? message.match(BARE_NUMBER)?.[1] ?? null;

const norm = (value?: string): string => (value ?? '').trim().toLowerCase();

const PENDING = new Set(['pending', 'pending-verification required', 'incomplete']);
const PAYMENT_FAILED = new Set(['payment declined', 'payment declined-adjusted', 'refund declined-adjusted']);
/** Listed under no bookings-page tab, so there is nowhere to link it to. */
const ERROR = 'error';
const ENDED = new Set(['declined', 'cancelled', 'expired']);

type BookingTab = 'ACTIVE' | 'PENDING' | 'INACTIVE' | 'FAILED';

type Timing = 'upcoming' | 'current' | 'ended' | 'unknown';

/** Where the booking sits in its dates, judged in the browser's day. */
const timingOf = (row: BookingLookupRow): Timing => {
  const start = moment(displayDate(row.startDate), SLASH_DAY, true);
  const end = moment(displayDate(row.endDate), SLASH_DAY, true);
  const today = moment().startOf('day');
  if (start.isValid() && today.isBefore(start)) return 'upcoming';
  if (end.isValid() && today.isAfter(end)) return 'ended';
  if (start.isValid()) return 'current';
  return 'unknown';
};

/**
 * The bookings-page tab the booking is listed under. Approved bookings are split
 * by whether they are running now, as the page does; everything else by status.
 */
const tabFor = (row: BookingLookupRow): BookingTab | null => {
  const status = norm(row.status);
  if (status === ERROR) return null;
  if (PENDING.has(status)) return 'PENDING';
  if (PAYMENT_FAILED.has(status)) return 'FAILED';
  if (ENDED.has(status)) return 'INACTIVE';
  return timingOf(row) === 'ended' ? 'INACTIVE' : 'ACTIVE';
};

const reasonLabel = (reason?: string): string => {
  const value = (reason ?? '').trim();
  if (!value) return '';
  // Buyer and supplier rows carry the enum name; staff rows the label itself.
  return (BookingReasonDeclinedLabel as Record<string, string>)[value] ?? value;
};

const declineSentence = (row: BookingLookupRow, audience: AssistantAudience): string => {
  const label = reasonLabel(row.reasonDeclined);
  const note = row.reasonDeclinedNote?.trim();
  if (label && note) return `The reason recorded was **${label}** \u2014 \u201c${note}\u201d.`;
  if (label) return `The reason recorded was **${label}**.`;
  if (note) return `The note recorded was: \u201c${note}\u201d.`;
  if (audience === 'buyer') {
    const who = row.supplierCompanyName ? `the supplier (**${row.supplierCompanyName}**)` : 'the supplier';
    return `No reason was recorded when it was declined; ${who} or SecurSpace support can tell you more.`;
  }
  return 'No reason was recorded when it was declined.';
};

const datesPhrase = (row: BookingLookupRow): string => {
  const timing = timingOf(row);
  const start = displayDate(row.startDate);
  const end = displayDate(row.endDate);
  if (timing === 'upcoming' && start) return `It starts on ${start}.`;
  if (timing === 'ended' && end) return `It ended on ${end}.`;
  if (timing === 'current') return end ? `It is active and runs until ${end}.` : 'It is active.';
  return '';
};

/** What the status means, for the person asking. */
const statusSentence = (row: BookingLookupRow, number: string, audience: AssistantAudience): string => {
  const status = norm(row.status);
  if (status === 'pending') {
    return audience === 'supplier'
      ? 'It is waiting for your approval \u2014 approve or decline it under **Bookings \u2192 Approvals**.'
      : 'It is waiting for the supplier to approve it.';
  }
  if (status === 'pending-verification required') {
    return 'It is pending and needs verification before it can be approved.';
  }
  if (status === 'incomplete') {
    return 'Checkout was never finished, so it is not a live booking.';
  }
  if (status === 'declined') return `It was declined. ${declineSentence(row, audience)}`;
  if (status === 'cancelled') return 'It was cancelled.';
  if (status === 'expired') return 'It expired.';
  if (PAYMENT_FAILED.has(status)) {
    return `Its latest payment failed \u2014 ask me _"why did payment fail on booking ${number}"_ for the cause and how to fix it.`;
  }
  if (status === ERROR) {
    return 'It is in an **error** state, which keeps it off every Bookings tab \u2014 SecurSpace support can look into it.';
  }
  if (status.startsWith('processing')) {
    return `A bank (ACH) payment on it is still processing. ${datesPhrase(row)}`.trim();
  }
  return datesPhrase(row);
};

const spacesPhrase = (row: BookingLookupRow): string => {
  const n = row.numberOfSpaces;
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return '';
  const type = enumLabel(row.assetType).toLowerCase();
  return `${n} ${n === 1 ? 'space' : 'spaces'}${type ? ` for ${type}` : ''}`;
};

const lead = (row: BookingLookupRow, number: string, audience: AssistantAudience): string => {
  const spaces = spacesPhrase(row);
  const at = row.locationName ? ` at **${row.locationName}**` : '';
  const supplier = audience !== 'supplier' && row.supplierCompanyName ? ` (${row.supplierCompanyName})` : '';
  const buyer = audience !== 'buyer' && row.buyerCompanyName ? ` for **${row.buyerCompanyName}**` : '';
  const dates = displayDateRange(row.startDate, row.endDate);
  const what = [spaces ? `**${spaces}**` : '', `${at}${supplier}${buyer}`.trim()].filter(Boolean).join(' ');
  return (
    `Booking **#${number}**${what ? ` \u2014 ${what}` : ''}${dates ? `, ${dates}` : ''}` +
    ` \u2014 is **${row.status || 'unknown'}**.`
  );
};

const yesNo = (value?: boolean | string): string => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return (value ?? '').trim();
};

const billingPhrase = (row: BookingLookupRow): string =>
  [enumLabel(row.durationType), enumLabel(row.frequency).toLowerCase()].filter(Boolean).join(', ');

const centsOrText = (value?: number | string): string =>
  typeof value === 'number' ? money(value) : (value ?? '').trim();

const approvedPhrase = (row: BookingLookupRow): string =>
  [displayDate(row.approvedOn), row.approvedBy ? `by ${row.approvedBy}` : ''].filter(Boolean).join(' ');

const rowsFor = (row: BookingLookupRow, number: string, audience: AssistantAudience): KnowledgeBreakdownRow[] => {
  const dates = displayDateRange(row.startDate, row.endDate);
  const adjusted = yesNo(row.endDateAdjusted) === 'Yes' ? ' (end date adjusted)' : '';
  const declined = norm(row.status) === 'declined'
    ? [reasonLabel(row.reasonDeclined), row.reasonDeclinedNote?.trim()].filter(Boolean).join(' \u2014 ') || 'None recorded'
    : '';

  const common: Array<[string, string | number | undefined]> = [
    ['Booking', `#${number}`],
    ['Status', [row.status, audience === 'admin' ? enumLabel(row.bookingSubscriptionStatus) : ''].filter(Boolean).join(' \u2014 ')],
    ['Location', row.locationName],
  ];

  if (audience === 'buyer') {
    return breakdownRows([
      ...common,
      ['Supplier', row.supplierCompanyName],
      ['Spaces', spacesPhrase(row)],
      ['Dates', dates && `${dates}${adjusted}`],
      ['Billing', billingPhrase(row)],
      ['First charge', centsOrText(row.initialCharge)],
      ['Recurring charge', centsOrText(row.recurringCharge)],
      ['Overages charged', yesNo(row.chargeOverages)],
      ['Payment method', row.paymentMethodDescription],
      ['Booked', displayDate(row.creationDate)],
      ['Approved', approvedPhrase(row)],
      ['Decline reason', declined],
    ]);
  }

  if (audience === 'supplier') {
    return breakdownRows([
      ...common,
      ['Customer', row.buyerCompanyName],
      ['Spaces', spacesPhrase(row)],
      ['Dates', dates && `${dates}${adjusted}`],
      ['Billing', billingPhrase(row)],
      ['First payout', money(row.initialSupplierPayoutAmount)],
      ['Recurring payout', money(row.recurringSupplierPayoutAmount)],
      ['Overages charged', yesNo(row.chargeOverages)],
      ['Booked', displayDate(row.creationDate)],
      ['Approved', approvedPhrase(row)],
      ['Decline reason', declined],
    ]);
  }

  return breakdownRows([
    ...common,
    ['Supplier', row.supplierCompanyName],
    ['Buyer', [row.buyerCompanyName, row.buyerEmail].filter(Boolean).join(' \u2014 ')],
    ['Spaces', spacesPhrase(row)],
    ['Dates', dates && `${dates}${adjusted}`],
    ['Billing', billingPhrase(row)],
    ['Rate per space', row.buyerRatePerSpace],
    ['First charge', centsOrText(row.initialCharge)],
    ['Overage rate', row.overageRate],
    ['Overages charged', yesNo(row.chargeOverages)],
    ['Brokered', yesNo(row.brokered)],
    ['Booked', displayDate(row.creationDate)],
    ['Decline reason', declined],
  ]);
};

/**
 * The booking on its audience's bookings page, on the tab it is listed under.
 * Staff have no bookings page that opens on one booking, so theirs goes to the
 * booking's invoices, which is where an investigation usually heads next.
 */
const bookingLink = (
  row: BookingLookupRow,
  number: string,
  audience: AssistantAudience,
): KnowledgeLink | undefined => {
  const encoded = encodeURIComponent(number);
  if (audience !== 'admin') {
    const tab = tabFor(row);
    if (!tab) return undefined;
    const base = audience === 'buyer' ? '/bookings' : '/bookings/supplier-bookings';
    return { label: 'Open this booking', path: `${base}?bookingNumber=${encoded}&status=${tab}` };
  }
  return {
    ...invoicePageLink('admin', { bookingNumber: number }, { from: row.creationDate }),
    label: 'Open this booking\u2019s invoices',
  };
};

export const answerBookingLookup = async ({ message, audience }: KnowledgeContext): Promise<KnowledgeAnswer> => {
  const number = bookingNumberIn(message);
  if (!number) {
    return {
      text:
        'Which booking do you mean? Tell me its booking number \u2014 for example _"status of ' +
        'booking 1021445"_ \u2014 and I will look it up.',
    };
  }

  const response = await searchBookingByNumber(audience, number);
  const rows = response?.body?.content ?? [];
  const row = rows.find((candidate) => norm(candidate.orderNumber) === norm(number));

  if (!row) {
    return {
      text:
        `I could not find booking **#${number}**${audience === 'admin' ? '' : ' on your account'}. ` +
        'Double-check the number \u2014 it is the **Booking Number** on the Bookings page. If it is ' +
        `an invoice number, ask me about _"invoice ${number}"_ instead.`,
    };
  }

  const status = statusSentence(row, number, audience);
  return {
    text: [lead(row, number, audience), status].filter(Boolean).join(' '),
    breakdown: rowsFor(row, number, audience),
    link: bookingLink(row, number, audience),
  };
};

export const bookingLookupQuery: KnowledgeQuery = {
  id: 'bookingLookup',
  audiences: ['admin', 'supplier', 'buyer'],
  synonyms: [
    'look up a booking',
    'lookup a booking',
    'booking lookup',
    'find a booking',
    'check on a booking',
    'status of a booking',
    'details of a booking',
    'look up a specific booking',
  ],
  claims: claimsLookup('booking'),
  needsLocations: false,
  example: 'status of booking 1021445',
  description:
    'Looks up one booking by its booking number (e.g. "status of booking 1021445", "who booked ' +
    '1021445", "when does booking 1021445 end", "why was booking 1021445 declined") and says ' +
    'what state it is in and what that means \u2014 active, upcoming, ended, awaiting approval, ' +
    'declined with the recorded reason, cancelled, or a failed payment \u2014 with its location, ' +
    'parties, spaces, dates and charges, and a link to it. About one named booking, not a count ' +
    'of bookings.',
  answer: answerBookingLookup,
};

export const __testing = { bookingNumberIn, tabFor, timingOf, statusSentence };
